<template>
    <!-- Chore preview / form -->
    <div v-if="msg.type === 'chore_form'" class="ce-card">
        <div class="ce-card__header">

            <div>
                <div class="ce-card__title">Chore Summary</div>
                <div class="ce-card__sub">{{ msg.payload?.response || 'Please review before saving' }}</div>
            </div>
        </div>
        <div class="ce-card__body">
            <div class="ce-row"><span class="ce-label">Title</span><span class="ce-value">{{ chorePre.choreTitle || '—' }}</span></div>
            <div class="ce-row"><span class="ce-label">Asset</span><span class="ce-value">{{ chorePre.assetName || '—' }}</span></div>
            <div class="ce-row"><span class="ce-label">Frequency</span><span class="ce-value">{{ chorePre.frequency || '—' }}</span></div>
            <div class="ce-row"><span class="ce-label">Due Date</span><span class="ce-value">{{ formatMMDDYYYY(chorePre.dueDate) }}</span></div>
            <div class="ce-row"><span class="ce-label">Assigned To</span><span class="ce-value ce-value--wrap">{{ joinNames(chorePre.roommateNames) }}</span></div>
            <!--<div class="ce-row ce-row--last"><span class="ce-label">Notes</span><span class="ce-value ce-value--wrap">{{ chorePre.notes || '—' }}</span></div>-->
        </div>
        <div class="ce-card__actions">
            <button type="button" class="ce-btn ce-btn--primary" :disabled="disabled" @click="$emit('confirm-chore', msg)">Save Chore</button>
            <button type="button" class="ce-btn ce-btn--link" :disabled="disabled" @click="$emit('cancel')">Cancel</button>
        </div>
    </div>

    <!-- Chore created / failed -->
    <div v-else-if="msg.type === 'chore_created' || msg.type === 'chore_failed'" class="ticket-preview-card">
        <div class="ticket-preview-header" :style="isChoreError ? HEADER_ERROR : null">

            <div>
                <h4>{{ isChoreError ? 'Chore Not Saved' : 'Chore Created!' }}</h4>
                <p>{{ isChoreError ? 'Something went wrong.' : 'Your chore has been scheduled.' }}</p>
            </div>
        </div>
        <div class="ticket-preview-body">
            <div class="ticket-preview-row"><span class="tp-label">Title</span><span class="tp-value">{{ choreData.title || '—' }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Asset</span><span class="tp-value">{{ choreData.assetName || '—' }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Frequency</span><span class="tp-value">{{ choreData.frequency || '—' }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Due</span><span class="tp-value">{{ formatMMDDYYYY(choreData.dueDate) }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Assigned To</span><span class="tp-value">{{ joinNames(choreData.roommateNames) }}</span></div>
            <div v-if="isChoreError" class="ticket-preview-error">

                <p><strong>Error:</strong> {{ choreData.error || 'Please try again.' }}</p>
            </div>
        </div>
        <div class="ticket-preview-footer">
            <span> {{ formatMMDDYYYY(currentDate) }}</span>
            <span :style="{ color: isChoreError ? '#dc2626' : '#16a34a' }">{{ isChoreError ? 'Not Saved' : 'Saved' }}</span>
        </div>
    </div>

    <!-- Chore list -->
    <div v-else-if="msg.type === 'view_chores'" class="ticket-preview-card">
        <div class="ticket-preview-header">

            <div><h4>{{ choreData.heading || 'Your Chores' }}</h4></div>
            <span class="ticket-preview-number">{{ chores.length }} chores</span>
        </div>
        <div class="ticket-preview-body ce-list-body">
            <div v-if="!chores.length" class="ce-empty">No chores found.</div>
            <div v-for="(c, idx) in chores" :key="c.id || idx" class="ce-list-item" :class="{ 'ce-list-item--last': idx === chores.length - 1 }">
                <div class="ce-list-item__main">
                    <div class="ce-list-item__title">{{ c.title }}</div>
                    <div class="ce-list-item__meta">{{ c.assetName || '—' }}</div>
                    <div class="ce-list-item__tags">
                        <span class="ce-pill" :style="pillStyle(choreStatusColor(c.status))">{{ c.status }}</span>
                        <span class="ce-list-item__due"> {{ formatMMDDYYYY(c.dueDate) }} · {{ c.frequency }}</span>
                    </div>

                    <div v-if="isChoreDetailsOpen(c, idx)" class="ce-chore-details">
                        <div class="ce-chore-detail-row"><span>Asset</span><strong>{{ c.assetName || '—' }}</strong></div>
                        <div class="ce-chore-detail-row"><span>Assigned To</span><strong>{{ c.assignedUserName || '—' }}</strong></div>
                        <div class="ce-chore-detail-row"><span>Status</span><strong>{{ c.status || '—' }}</strong></div>
                        <div class="ce-chore-detail-row"><span>Due Date</span><strong>{{ formatMMDDYYYY(c.dueDate) }}</strong></div>
                        <div class="ce-chore-detail-row"><span>Frequency</span><strong>{{ c.frequency || '—' }}</strong></div>
                    </div>
                </div>

                <div class="ce-list-item__actions">
                    <button type="button"
                            class="ce-btn ce-btn--accent-link"
                            :disabled="disabled"
                            @click="toggleChoreDetails(c, idx)">
                        {{ isChoreDetailsOpen(c, idx) ? 'Hide ▲' : 'Details ▼' }}
                    </button>
                    <button type="button"
                            class="ce-btn ce-btn--danger-link"
                            :disabled="disabled"
                            @click="$emit('delete-chore', c)">
                        Remove
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Chore frequency picker -->
    <div v-else-if="msg.type === 'chore_frequency_options'" class="ce-card">
        <div class="ce-card__header">

            <div>
                <div class="ce-card__title">Chore Frequency</div>
                <div class="ce-card__sub">{{ msg.payload?.response || 'How often should this chore repeat?' }}</div>
            </div>
        </div>
        <div class="ce-card__body">
            <button v-for="option in (msg.payload?.chore_data?.options || [])"
                    :key="option.value || option.id || option"
                    type="button"
                    class="ce-btn ce-btn--primary"
                    style="width:100%; margin-bottom:8px;"
                    :disabled="disabled"
                    @click="$emit('select-chore-frequency', option.value || option.text || option)">
                {{ option.text || option.name || option }}
            </button>
        </div>
    </div>

    <!-- Chore roommate picker -->
    <div v-else-if="msg.type === 'chore_roommate_options'" class="ce-card">
        <div class="ce-card__header">
            <span class="ce-card__icon"></span>
            <div>
                <div class="ce-card__title">Assign Roommates</div>
                <div class="ce-card__sub">{{ msg.payload?.response || 'Select roommates for this chore' }}</div>
            </div>
        </div>
        <div class="ce-card__body">

            <!-- Select All -->
            <label class="ce-roommate-select-all">
                <input type="checkbox"
                       :checked="allChoreRoommatesSelected"
                       :disabled="disabled || !choreRoommateOptions.length"
                       @change="toggleAllChoreRoommates" />
                <span>Select All</span>
            </label>

            <div class="ce-roommate-divider"></div>

            <!-- Existing roommate list -->
            <label v-for="roommate in choreRoommateOptions"
                   :key="roommate.id || roommate.value"
                   class="ce-roommate-option">
                <input v-model="selectedChoreRoommateIds"
                       type="checkbox"
                       :value="String(roommate.id || roommate.value)"
                       :disabled="disabled" />
                <span>{{ roommate.name || roommate.text }}</span>
            </label>
        </div>
        <div class="ce-card__actions">
            <button type="button"
                    class="ce-btn ce-btn--primary"
                    :disabled="disabled || !selectedChoreRoommateIds.length"
                    @click="$emit('select-chore-roommates', selectedChoreRoommateIds)">
                Confirm Selection
            </button>
            <button type="button" class="ce-btn ce-btn--link" :disabled="disabled" @click="$emit('cancel')">Cancel</button>
        </div>
    </div>

    <!-- Event Guest Phone Number -->
    <div v-else-if="msg.type === 'event_flow' && isEventGuestPhonePrompt"
         class="ce-phone-card">

        <div class="ce-phone-question"
             v-html="formatEventPhonePrompt(responseText)">
        </div>

        <div class="ce-phone-row">
            <Dropdown v-model="eventGuestPhone.countryCode"
                      :options="countryCodeList"
                      optionValue="value"
                      optionLabel="text"
                      placeholder="Code"
                      filter
                      :disabled="disabled"
                      class="ce-country-dropdown"
                      @change="onEventGuestCountryCodeChange" />

            <input v-model="eventGuestPhone.phoneNumber"
                   type="text"
                   inputmode="numeric"
                   class="ce-phone-input"
                   :disabled="disabled"
                   :maxlength="eventGuestPhoneLength === 10 ? eventGuestPhoneLength + 4 : eventGuestPhoneLength"
                   placeholder="Phone number"
                   @input="onEventGuestPhoneInput" />
        </div>

        <div v-if="eventGuestPhoneError" class="ce-phone-error">
            {{ eventGuestPhoneError }}
        </div>

        <button type="button"
                class="ce-btn ce-btn--primary ce-phone-submit"
                :disabled="disabled"
                @click="submitEventGuestPhone">
            Continue
        </button>
    </div>

    <!-- Event Guest Invitation Method -->
    <div v-else-if="msg.type === 'event_guest_invite_prompt'"
         class="ce-phone-card ce-invite-card">

        <div class="ce-phone-question">
            {{ invitePromptText }}
        </div>

        <div class="ce-invite-choice-list">
            <button type="button"
                    class="ce-invite-choice-btn"
                    :disabled="disabled"
                    @click="$emit('select-event-guest-invite', 'sms')">
                <span>SMS</span>
            </button>

            <button type="button"
                    class="ce-invite-choice-btn"
                    :disabled="disabled"
                    @click="$emit('select-event-guest-invite', 'email')">
                <span>Email</span>
            </button>
        </div>
    </div>

    <!-- Other Event Flow text -->
    <div v-else-if="msg.type === 'event_flow'"
         class="ce-text-message">
        <span v-html="formatEventPhonePrompt(responseText)"></span>
    </div>

    <!-- Event preview (editable address like Event/addedit) -->
    <div v-else-if="msg.type === 'event_preview'" class="ce-card ce-card--form">
        <div class="ce-card__header">

            <div>
                <div class="ce-card__title">Event Summary</div>
                <div class="ce-card__sub">{{ msg.payload?.response || 'Please review before creating' }}</div>
            </div>
        </div>
        <div class="ce-card__body">
            <div class="ce-row"><span class="ce-label">Title</span><span class="ce-value">{{ eventForm.title || '—' }}</span></div>
            <div class="ce-row"><span class="ce-label">Entry Fee</span><span class="ce-value">{{ eventForm.entryFee != null ? ('$' + Number(eventForm.entryFee).toFixed(2)) : '—' }}</span></div>
            <div class="ce-row"><span class="ce-label">Date</span><span class="ce-value">{{ formatDate(eventForm.eventDate) }}</span></div>
            <div class="ce-row"><span class="ce-label">Start Time</span><span class="ce-value">{{ formatTime(eventForm.startTime) }}</span></div>

            <div class="ce-addr-title">Address</div>
            <div class="ce-field">
                <label>Street Address 1</label>
                <textarea v-model="eventForm.addressLine1"
                          class="ce-input"
                          rows="2"
                          :disabled="disabled"
                          placeholder="Street Address 1"></textarea>
            </div>
            <div class="ce-field">
                <label>Street Address 2</label>
                <textarea v-model="eventForm.addressLine2"
                          class="ce-input"
                          rows="2"
                          :disabled="disabled"
                          placeholder="Street Address 2"></textarea>
            </div>
            <div class="ce-field">
                <label>Country</label>
                <Dropdown v-model="eventForm.country"
                          :options="countryList"
                          optionValue="value"
                          optionLabel="text"
                          filter
                          showClear
                          placeholder="Select country"
                          class="w-100 ce-dropdown"
                          :disabled="disabled"
                          @change="onCountryChange" />
            </div>
            <div class="ce-field" v-if="eventForm.country === US_COUNTRY_ID">
                <label>State</label>
                <Dropdown v-model="eventForm.state"
                          :options="stateList"
                          optionValue="value"
                          optionLabel="text"
                          filter
                          showClear
                          placeholder="Select state"
                          class="w-100 ce-dropdown"
                          :disabled="disabled" />
            </div>
            <div class="ce-field" v-else>
                <label>State</label>
                <input v-model="eventForm.stateName"
                       type="text"
                       class="ce-input"
                       maxlength="200"
                       :disabled="disabled"
                       placeholder="Enter state" />
            </div>
            <div class="ce-field">
                <label>City</label>
                <input v-model="eventForm.city"
                       type="text"
                       class="ce-input"
                       :disabled="disabled"
                       placeholder="City" />
            </div>
            <div class="ce-field">
                <label>Zip Code</label>
                <input v-model="eventForm.zip"
                       type="text"
                       class="ce-input"
                       maxlength="10"
                       :disabled="disabled"
                       placeholder="Zip code" />
            </div>
        </div>
        <div class="ce-card__actions">
            <button type="button" class="ce-btn ce-btn--primary" :disabled="disabled" @click="onConfirmEvent">Confirm &amp; Create</button>
            <button type="button" class="ce-btn ce-btn--link" :disabled="disabled" @click="$emit('cancel')">Cancel</button>
        </div>
    </div>

    <!-- Event created / failed -->
    <div v-else-if="msg.type === 'event_created' || msg.type === 'event_failed'" class="ticket-preview-card">
        <div class="ticket-preview-header" :style="isEventError ? HEADER_ERROR : null">

            <div>
                <h4>{{ isEventError ? 'Event Failed' : 'Event Created! ' }}</h4>
                <p>{{ isEventError ? 'Something went wrong.' : 'Your event is live.' }}</p>
            </div>

        </div>
        <div class="ticket-preview-body">
            <div class="ticket-preview-row"><span class="tp-label">Title</span><span class="tp-value">{{ eventData.title || '—' }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Date</span><span class="tp-value">{{ formatDate(eventData.eventDate) }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Start Time</span><span class="tp-value">{{ formatTime(eventData.startTime) || '—' }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Entry Fee</span><span class="tp-value">${{ Number(eventData.entryFee || 0).toFixed(2) }}</span></div>
            <div v-if="isEventError" class="ticket-preview-error">

                <p><strong>Error:</strong> {{ eventData.error || 'Please try again.' }}</p>
            </div>
        </div>
        <div v-if="!isEventError && eventData.eventId" class="ce-card__pad">
            <button type="button"
                    class="ce-btn ce-btn--secondary"
                    :disabled="disabled"
                    @click="$emit('add-guest', { eventId: eventData.eventId, title: eventData.title })">
                Add Guest
            </button>
        </div>
        <div class="ticket-preview-footer">
            <span> {{ formatMMDDYYYY(currentDate) }}</span>
            <span :style="{ color: isEventError ? '#dc2626' : '#16a34a' }">{{ isEventError ? 'Not Saved' : 'Saved' }}</span>
        </div>
    </div>

    <!-- Event list -->
    <div v-else-if="msg.type === 'view_events'" class="ticket-preview-card">
        <div class="ticket-preview-header">

            <div><h4>{{ eventData.heading || 'Your Events' }}</h4></div>
        </div>
        <div class="ticket-preview-body ce-list-body">
            <div v-if="!events.length" class="ce-empty">No events found.</div>
            <div v-for="(e, idx) in events" :key="e.eventId || idx" class="ce-list-item" :class="{ 'ce-list-item--last': idx === events.length - 1 }">
                <div class="ce-list-item__main">
                    <div class="ce-list-item__title">{{ e.title }}</div>
                    <div class="ce-list-item__meta"> {{ formatShortDate(e.eventDate) }} · {{ formatTimeNoSeconds(e.startTime) }} · ${{ Number(e.entryFee || 0).toFixed(2) }}</div>
                    <div class="ce-list-item__tags">
                        <span class="ce-pill" :style="pillStyle(e.isCancelled ? '#dc2626' : '#16a34a')">{{ e.isCancelled ? 'Cancelled' : 'Upcoming' }}</span>
                        <span class="ce-list-item__due"> {{ e.confirmed }}/{{ e.totalSent }} confirmed</span>
                    </div>
                </div>
                <div class="ce-list-item__actions">
                    <button type="button" class="ce-btn ce-btn--accent-link" :disabled="disabled" @click="$emit('event-detail', e)">Details ▼</button>
                    <button type="button" class="ce-btn ce-btn--success-link" :disabled="disabled" @click="$emit('add-guest', e)">Add Guest</button>
                    <button type="button"
                            class="ce-btn ce-btn--warn-link"
                            :disabled="disabled"
                            @click="$emit('cancel-event', e)">
                        {{ e.isCancelled ? 'Reopen' : 'Cancel' }}
                    </button>
                    <button type="button" class="ce-btn ce-btn--danger-link" :disabled="disabled" @click="$emit('delete-event', e)">🗑 Delete</button>
                </div>
            </div>
        </div>
    </div>

    <!-- Duplicate Event List -->
    <div v-else-if="msg.type === 'view_events_duplicate'" class="ticket-preview-card">
        <div class="ticket-preview-header">
            <div>
                <h4>{{ eventData.heading || 'Select an Event to Duplicate' }}</h4>
            </div>
            <span class="ticket-preview-number">
                {{ events.length }} event{{ events.length === 1 ? '' : 's' }}
            </span>
        </div>

        <div class="ticket-preview-body ce-list-body">
            <div v-if="!events.length" class="ce-empty">
                No events found.
            </div>

            <div v-for="(e, idx) in events"
                 :key="e.eventId || idx"
                 class="ce-list-item"
                 :class="{ 'ce-list-item--last': idx === events.length - 1 }">

                <div class="ce-list-item__main">
                    <div class="ce-list-item__title">{{ e.title }}</div>

                    <div class="ce-list-item__meta">
                        {{ formatShortDate(e.eventDate) }}
                        · {{ formatTimeNoSeconds(e.startTime) }}
                        · ${{ Number(e.entryFee || 0).toFixed(2) }}
                    </div>

                    <div class="ce-list-item__tags">
                        <span class="ce-pill"
                              :style="pillStyle(e.isCancelled ? '#dc2626' : '#16a34a')">
                            {{ e.isCancelled ? 'Cancelled' : 'Upcoming' }}
                        </span>

                        <span class="ce-list-item__due">
                            {{ e.confirmed }}/{{ e.totalSent }} confirmed
                        </span>
                    </div>
                </div>

                <div class="ce-list-item__actions">
                    <button type="button"
                            class="ce-duplicate-btn"
                            :disabled="disabled"
                            @click="$emit('duplicate-event', e)">
                        Duplicate
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Event detail -->
    <div v-else-if="msg.type === 'event_detail'" class="ticket-preview-card">
        <div class="ticket-preview-header">

            <div>
                <h4>{{ eventDetail.title || 'Event' }}</h4>
                <p>{{ eventDetail.message || '' }}</p>
            </div>
        </div>
        <div class="ticket-preview-body">
            <div class="ticket-preview-row"><span class="tp-label">Date</span><span class="tp-value">{{ formatDate(eventDetail.eventDate) }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Time</span><span class="tp-value">{{ formatTimeNoSeconds(eventDetail.startTime) }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Entry Fee</span><span class="tp-value">${{ Number(eventDetail.entryFee || 0).toFixed(2) }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Address</span><span class="tp-value">{{ eventAddress }}</span></div>
            <div class="ce-guest-heading">GUESTS ({{ guests.length }})</div>
            <div v-if="!guests.length" class="ce-empty">No guests yet.</div>
            <div v-for="(g, gi) in guests" :key="gi" class="ce-guest-row">
                <div>
                    <div class="ce-guest-name"> {{ g.fullName }}</div>
                    <div class="ce-guest-contact">{{ g.email || '' }} {{ g.phone || '' }}</div>
                </div>
                <div class="ce-list-item__tags">
                    <span class="ce-pill" :style="pillStyle(eventRsvpColor(g.rsvp))">{{ g.rsvp }}</span>
                    <span class="ce-pill" :style="pillStyle('#0369a1')">{{ g.payment }}</span>
                </div>
            </div>
        </div>
        <div class="ce-card__pad">
            <button type="button"
                    class="ce-btn ce-btn--secondary"
                    :disabled="disabled"
                    @click="$emit('add-guest', { eventId: eventDetail.eventId, title: eventDetail.title })">
                Add Guest
            </button>
        </div>
    </div>
</template>

<script>import Dropdown from 'primevue/dropdown';
    import UserService from '@/service/user.service.js';
    import {
        findDefaultUsCountryCode,
        resolvePhoneLength,
    } from '@/helpers/phoneCountryHelpers.js';
    import {
        formatChoresEventsDate,
        formatChoresEventsShortDate,
        formatChoresEventsTime,
        choreStatusColor,
        eventRsvpColor,
        joinNames,
        buildEventAddress,
    } from './choresEvents.helpers';

    const US_COUNTRY_ID = '5c94ae54-5519-4004-becd-3366bbd6e1a9';
    const HEADER_ERROR = 'background:linear-gradient(135deg,#991b1b,#dc2626);';

    function formatMMDDYYYY(value) {
        if (!value) return '—';
        const text = String(value).trim();
        const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (iso) return `${iso[2]}-${iso[3]}-${iso[1]}`;
        const date = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
        if (date) return `${date[1].padStart(2, '0')}-${date[2].padStart(2, '0')}-${date[3]}`;
        return text;
    }

    function emptyEventForm() {
        return {
            title: '',
            entryFee: null,
            eventDate: '',
            startTime: '',
            addressLine1: '',
            addressLine2: '',
            country: US_COUNTRY_ID,
            state: null,
            stateName: '',
            city: '',
            zip: '',
            message: '',
            companyId: null,
            actorUserId: null,
        };
    }

    export default {
        name: 'ChatbotChoresEvents',
        components: {
            Dropdown,
        },
        props: {
            msg: { type: Object, required: true },
            disabled: { type: Boolean, default: false },
            currentDate: { type: String, default: '' },
        },
        emits: [
            'confirm-chore',
            'delete-chore',
            'confirm-event',
            'cancel',
            'add-guest',
            'event-detail',
            'cancel-event',
            'delete-event',
            'duplicate-event',
            'select-event-guest-invite',
            'select-chore-frequency',
            'select-chore-roommates',
            'submit-event-guest-phone',
        ],
        data() {
            return {
                US_COUNTRY_ID,
                HEADER_ERROR,
                eventForm: emptyEventForm(),
                countryList: [],
                stateList: [],
                selectedChoreRoommateIds: [],
                expandedChoreKey: '',
                _formInitKey: '',
                _addressLoadedKey: '',

                countryCodeList: [],
                eventGuestPhone: {
                    countryCode: '',
                    phoneNumber: '',
                },
                eventGuestPhoneLength: 10,
                eventGuestPhoneError: '',
            };
        },
        async created() {
            await this.GetCountryCodeList();
        },
        computed: {
            responseText() {
                return this.msg?.payload?.response || this.msg?.text || '';
            },

            invitePromptText() {
                return String(this.responseText || '')
                    .replace(/\*\*/g, '')
                    .trim() || 'Please choose Email or SMS to send the invitation.';
            },
            isEventGuestPhonePrompt() {
                if (this.msg?.type !== 'event_flow') return false;

                const text = String(this.responseText || '')
                    .replace(/<[^>]*>/g, ' ')
                    .replace(/\*\*/g, '')
                    .toLowerCase();

                return text.includes('phone number')
                    || text.includes('phone no')
                    || text.includes("guest's phone")
                    || text.includes('guest’s phone');
            },
            chorePre() {
                return this.msg?.payload?.chore_data?.prefilled || {};
            },
            choreData() {
                return this.msg?.payload?.chore_data || {};
            },
            chores() {
                return this.choreData.chores || [];
            },
            isChoreError() {
                return this.msg?.type === 'chore_failed';
            },

            choreRoommateOptions() {
                return this.msg?.payload?.chore_data?.options || [];
            },

            allChoreRoommatesSelected() {
                if (!this.choreRoommateOptions.length) return false;

                const allIds = this.choreRoommateOptions.map((roommate) =>
                    String(roommate.id || roommate.value)
                );

                return allIds.every((id) =>
                    this.selectedChoreRoommateIds.includes(id)
                );
            },

            eventPre() {
                return this.msg?.payload?.event_data?.prefilled || {};
            },
            eventData() {
                return this.msg?.payload?.event_data || {};
            },
            events() {
                return this.eventData.events || [];
            },
            isEventError() {
                return this.msg?.type === 'event_failed';
            },
            eventDetail() {
                return this.eventData.detail || {};
            },
            guests() {
                return this.eventData.guests || [];
            },
            eventAddress() {
                return buildEventAddress(this.eventDetail);
            },
        },
        watch: {
            msg: {
                immediate: true,
                deep: true,
                handler(m) {
                    if (m?.type === 'event_preview') {
                        this.initEventFormFromPayload();
                        this.loadEventAddressLikePortal();
                    }
                    if (m?.type === 'chore_roommate_options') {
                        this.selectedChoreRoommateIds = [];
                    }
                },
            },
        },
        methods: {
            joinNames,
            choreStatusColor,
            eventRsvpColor,
            formatDate: formatMMDDYYYY,
            formatShortDate: formatMMDDYYYY,
            formatMMDDYYYY,

            choreKey(chore, index) {
                return String(chore?.id || chore?.Id || index);
            },

            toggleChoreDetails(chore, index) {
                const key = this.choreKey(chore, index);
                this.expandedChoreKey = this.expandedChoreKey === key ? '' : key;
            },

            isChoreDetailsOpen(chore, index) {
                return this.expandedChoreKey === this.choreKey(chore, index);
            },

            toggleAllChoreRoommates(event) {
                if (this.disabled) return;

                if (event?.target?.checked) {
                    this.selectedChoreRoommateIds =
                        this.choreRoommateOptions.map((roommate) =>
                            String(roommate.id || roommate.value)
                        );
                } else {
                    this.selectedChoreRoommateIds = [];
                }
            },

            formatEventPhonePrompt(text) {
                return String(text || '')
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            },

            async GetCountryCodeList() {
                try {
                    const response = await UserService.countryList(
                        'countryCode',
                        '00Test',
                        localStorage.getItem('companyId')
                    );

                    this.countryCodeList = response?.data || [];

                    const us = findDefaultUsCountryCode(this.countryCodeList)
                        || this.countryCodeList.find((item) =>
                            String(item.text || '').includes('US')
                        );

                    if (us) {
                        this.eventGuestPhone.countryCode = us.value;
                        this.eventGuestPhoneLength =
                            resolvePhoneLength(us.value, this.countryCodeList) || 10;
                    }
                } catch (e) {
                    console.error('Event guest country code load failed', e);
                }
            },

            onEventGuestCountryCodeChange() {
                this.eventGuestPhoneLength =
                    resolvePhoneLength(
                        this.eventGuestPhone.countryCode,
                        this.countryCodeList
                    ) || 10;

                this.eventGuestPhone.phoneNumber = '';
                this.eventGuestPhoneError = '';
            },

            onEventGuestPhoneInput(event) {
                const maxLength = this.eventGuestPhoneLength || 10;

                const digits = String(event?.target?.value || '')
                    .replace(/\D/g, '')
                    .slice(0, maxLength);

                let formatted = digits;

                if (maxLength === 10) {
                    if (digits.length > 6) {
                        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                    } else if (digits.length > 3) {
                        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                    } else if (digits.length > 0) {
                        formatted = `(${digits}`;
                    }
                }

                this.eventGuestPhone.phoneNumber = formatted;
                this.eventGuestPhoneError = '';
            },

            resolveEventGuestDialCode() {
                const value = this.eventGuestPhone.countryCode;
                if (!value) return '';

                const selected = (this.countryCodeList || [])
                    .find((item) => item.value === value);

                if (!selected || !selected.text) return value;

                const dialCode = String(selected.text).trim().split(/\s+/)[0];
                return dialCode || value;
            },

            submitEventGuestPhone() {
                this.eventGuestPhoneError = '';

                if (!this.eventGuestPhone.countryCode) {
                    this.eventGuestPhoneError = 'Please select a country code.';
                    return;
                }

                const digits = String(this.eventGuestPhone.phoneNumber || '')
                    .replace(/\D/g, '');

                const requiredLength = this.eventGuestPhoneLength || 10;

                if (digits.length !== requiredLength) {
                    this.eventGuestPhoneError =
                        `Enter a valid ${requiredLength}-digit phone number.`;
                    return;
                }

                const dialCode = this.resolveEventGuestDialCode();

                let formattedPhone = digits;

                if (requiredLength === 10) {
                    formattedPhone =
                        `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                }

                const fullPhoneNumber = dialCode
                    ? `${dialCode} ${formattedPhone}`
                    : formattedPhone;

                this.$emit('submit-event-guest-phone', fullPhoneNumber);
            },

            formatTimeNoSeconds(value) {
                if (!value) return '—';

                const parts = String(value).trim().split(':');
                if (parts.length < 2) return String(value);

                return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
            },

            formatTime(value) {
                if (!value) return '—';

                const parts = String(value).split(':');
                if (parts.length < 2) return value;

                let hours = parseInt(parts[0], 10);
                const minutes = parts[1];

                const period = hours >= 12 ? 'PM' : 'AM';
                hours = hours % 12 || 12;

                return `${String(hours).padStart(2, '0')} : ${minutes} ${period}`;
            },
            pillStyle(color) {
                return {
                    background: `${color}1a`,
                    color,
                    border: `1px solid ${color}55`,
                };
            },
            async loadEventAddressLikePortal() {
                // Same data sources as Event/addedit.vue create flow
                await this.ensureAddressLists();
                await this.loadUserAddressFromProfile();
            },
            async ensureAddressLists() {
                if (this.countryList.length && this.stateList.length) return;
                try {
                    const [countries, states] = await Promise.all([
                        UserService.countryList('country', '00Test', localStorage.getItem('userId')),
                        UserService.getStateList(),
                    ]);
                    this.countryList = countries?.data || [];
                    this.stateList = states?.data || [];
                } catch (e) {
                    console.error('ChatbotChoresEvents address lists', e);
                }
            },
            /**
             * Prefill address from logged-in user profile — same as portal Create Event (GetUserData).
             * Do NOT use AI/chatbot addressLine1.
             */
            async loadUserAddressFromProfile() {
                const userId = localStorage.getItem('userId');
                if (!userId) return;
                // Avoid re-fetching for the same preview card
                if (this._addressLoadedKey === this._formInitKey) return;
                try {
                    const response = await UserService.getUserDetail(userId, 'optionalTest');
                    const result = response?.data;
                    if (!result) return;

                    let stateName = null;
                    if (result.county != US_COUNTRY_ID) {
                        stateName = result.stateName;
                    }

                    this.eventForm.addressLine1 = result.address ? result.address : '';
                    this.eventForm.addressLine2 = result.address2 ? result.address2 : '';
                    this.eventForm.city = result.city ? result.city : '';
                    this.eventForm.zip = result.zipCode ? result.zipCode : '';
                    this.eventForm.country = result.county ? result.county : US_COUNTRY_ID;
                    this.eventForm.state = stateName == null ? (result.state || null) : null;
                    this.eventForm.stateName = stateName || '';
                    this._addressLoadedKey = this._formInitKey;
                } catch (e) {
                    console.error('ChatbotChoresEvents GetUserData', e);
                }
            },
            initEventFormFromPayload() {
                const pre = this.eventPre || {};
                // Re-init only when a new preview card arrives (ignore AI address)
                const key = `${pre.title}|${pre.eventDate}|${pre.startTime}|${pre.entryFee}`;
                if (key === this._formInitKey && this.eventForm.title) return;
                this._formInitKey = key;
                this._addressLoadedKey = '';

                this.eventForm = {
                    ...emptyEventForm(),
                    title: pre.title || '',
                    entryFee: pre.entryFee != null ? pre.entryFee : null,
                    eventDate: pre.eventDate || '',
                    startTime: pre.startTime || '',
                    // Address comes from user profile API (portal Create Event), not AI
                    addressLine1: '',
                    addressLine2: '',
                    country: US_COUNTRY_ID,
                    state: null,
                    stateName: '',
                    city: '',
                    zip: '',
                    message: pre.message || '',
                    companyId: pre.companyId || null,
                    actorUserId: pre.actorUserId || localStorage.getItem('userId'),
                };
            },
            onCountryChange() {
                this.eventForm.state = null;
                this.eventForm.stateName = '';
            },
            buildEventSubmitPayload() {
                const f = this.eventForm;
                const state =
                    f.country === US_COUNTRY_ID
                        ? (f.state || null)
                        : ((f.stateName || '').trim() || null);

                return {
                    ...this.eventPre,
                    title: f.title,
                    entryFee: f.entryFee,
                    eventDate: f.eventDate,
                    startTime: f.startTime,
                    addressLine1: (f.addressLine1 || '').trim() || null,
                    addressLine2: (f.addressLine2 || '').trim() || null,
                    country: f.country || null,
                    city: (f.city || '').trim() || null,
                    state,
                    stateName: f.country === US_COUNTRY_ID ? null : ((f.stateName || '').trim() || null),
                    zip: (f.zip || '').trim() || null,
                    message: f.message || '',
                    companyId: f.companyId,
                    actorUserId: f.actorUserId,
                };
            },
            onConfirmEvent() {
                const payload = this.buildEventSubmitPayload();
                if (this.msg?.payload?.event_data) {
                    this.msg.payload.event_data.prefilled = {
                        ...(this.msg.payload.event_data.prefilled || {}),
                        ...payload,
                    };
                }
                this.$emit('confirm-event', this.msg);
            },
        },
    };</script>

<style scoped>
        .ce-card {
            background: #fff;
            border-radius: 16px;
            box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
            overflow: hidden;
            font-size: 13px;
            min-width: 280px;
            max-width: 360px;
        }

        .ce-card--form {
            max-width: 380px;
        }

        .ce-card__header {
            /* Match chatbot ticket-preview-header blue */
            background: linear-gradient(135deg, rgb(15, 102, 184), rgb(18, 35, 65));
            color: #fff;
            padding: 14px 18px;
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .ce-card__icon {
            font-size: 22px;
        }

        .ce-card__title {
            font-weight: 700;
            font-size: 14px;
        }

        .ce-card__sub {
            font-size: 11px;
            opacity: 0.8;
        }

        .ce-card__body {
            padding: 14px 18px;
            display: flex;
            flex-direction: column;
            gap: 10px;
        }

        .ce-row {
            display: flex;
            justify-content: space-between;
            border-bottom: 1px solid #f0f0f0;
            padding-bottom: 8px;
            gap: 12px;
        }

        .ce-row--last {
            border-bottom: none;
            padding-bottom: 0;
        }

        .ce-label {
            color: #888;
            font-size: 11px;
            font-weight: 600;
            flex-shrink: 0;
        }

        .ce-value {
            color: #1e2d45;
            font-weight: 600;
            font-size: 12px;
            text-align: right;
        }

        .ce-value--wrap {
            max-width: 200px;
        }

        .ce-addr-title {
            font-size: 12px;
            font-weight: 700;
            color: #1e2d45;
            margin-top: 4px;
            padding-top: 4px;
            border-top: 1px dashed #e5e7eb;
        }

        .ce-field {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        .ce-field--last {
            margin-bottom: 0;
        }

        .ce-field label {
            color: #888;
            font-size: 11px;
            font-weight: 600;
        }

        .ce-input {
            width: 100%;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            padding: 8px 10px;
            font-size: 12px;
            color: #1e2d45;
            background: #fff;
            resize: vertical;
            box-sizing: border-box;
        }

            .ce-input:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }

        .ce-dropdown {
            width: 100%;
            font-size: 12px;
        }

        .ce-card__actions {
            padding: 0 18px 16px;
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .ce-card__pad {
            padding: 0 16px 12px;
        }

        .ce-btn {
            border: none;
            border-radius: 10px;
            padding: 10px;
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
        }

            .ce-btn:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }

        .ce-btn--primary {
            background: linear-gradient(135deg, rgb(15, 102, 184), rgb(18, 35, 65));
            color: #fff;
        }

        .ce-btn--secondary {
            width: 100%;
            background: #f1f5f9;
            color: #1e2d45;
            border: 1.5px solid #cbd5e1;
            font-weight: 600;
        }

        .ce-btn--link {
            background: none;
            color: #aaa;
            font-size: 12px;
            font-weight: 500;
            text-decoration: underline;
            padding: 4px;
        }

        .ce-btn--danger-link,
        .ce-btn--accent-link,
        .ce-btn--success-link,
        .ce-btn--warn-link {
            background: none;
            border: none;
            font-size: 11px;
            font-weight: 600;
            cursor: pointer;
            padding: 2px 0;
            text-align: left;
        }

        .ce-btn--danger-link {
            color: #dc2626;
        }

        .ce-btn--accent-link {
            color: rgb(15, 102, 184);
        }

        .ce-btn--success-link {
            color: #16a34a;
        }

        .ce-btn--warn-link {
            color: #d97706;
        }

        .ce-list-body {
            padding: 0 16px 8px !important;
        }

        .ce-list-item {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 8px;
            padding: 10px 0;
            border-bottom: 1px solid var(--clr-border, #e5e7eb);
        }

        .ce-list-item--last {
            border-bottom: none;
        }

        .ce-list-item__main {
            flex: 1;
            min-width: 0;
        }

        .ce-list-item__title {
            font-weight: 600;
            font-size: 13px;
        }

        .ce-list-item__meta {
            font-size: 11px;
            color: var(--clr-text-muted, #888);
            margin-top: 3px;
        }

        .ce-list-item__tags {
            margin-top: 4px;
            display: flex;
            gap: 6px;
            align-items: center;
            flex-wrap: wrap;
        }

        .ce-list-item__due {
            font-size: 11px;
            color: var(--clr-text-muted, #888);
        }

        .ce-list-item__actions {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 3px;
            flex-shrink: 0;
        }

        .ce-chore-details {
            margin-top: 8px;
            padding: 8px 10px;
            border: 1px solid var(--clr-border, #e5e7eb);
            border-radius: 7px;
            background: #f8fafc;
        }

        .ce-chore-detail-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 10px;
            padding: 4px 0;
            border-bottom: 1px solid #e5e7eb;
            font-size: 10px;
        }

            .ce-chore-detail-row:last-child {
                border-bottom: 0;
            }

            .ce-chore-detail-row > span {
                color: var(--clr-text-muted, #888);
                font-weight: 600;
            }

            .ce-chore-detail-row > strong {
                min-width: 0;
                color: #1e2d45;
                font-weight: 600;
                text-align: right;
                overflow-wrap: anywhere;
            }

        .ce-pill {
            padding: 1px 8px;
            border-radius: 10px;
            font-size: 10px;
            font-weight: 600;
        }

        .ce-empty {
            text-align: center;
            color: #999;
            font-size: 12px;
            padding: 16px 0;
        }

        .ce-guest-heading {
            margin-top: 8px;
            font-size: 11px;
            font-weight: 700;
            color: #888;
        }

        .ce-guest-row {
            display: grid;
            grid-template-columns: minmax(0, 1fr) auto;
            align-items: start;
            gap: 10px;
            padding: 10px 0;
            border-bottom: 1px dashed #e5e7eb;
        }

            .ce-guest-row > div:first-child {
                min-width: 0;
            }

            .ce-guest-row .ce-list-item__tags {
                margin-top: 0;
                display: flex;
                flex-direction: column;
                align-items: flex-end;
                gap: 5px;
                flex-wrap: nowrap;
            }

        .ce-guest-name {
            margin-bottom: 3px;
            color: #1e2d45;
            font-weight: 700;
            font-size: 12px;
            line-height: 1.3;
        }

        .ce-guest-contact {
            max-width: 100%;
            color: var(--clr-text-muted, #7b8794);
            font-size: 10px;
            line-height: 1.45;
            overflow-wrap: anywhere;
            word-break: normal;
        }

        .ce-guest-row .ce-pill {
            min-width: 64px;
            padding: 2px 8px;
            text-align: center;
            box-sizing: border-box;
            white-space: nowrap;
        }

        /* Chore roommate selector */
        .ce-roommate-select-all,
        .ce-roommate-option {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            color: #1e2d45;
            font-size: 13px;
            line-height: 1.4;
        }

        .ce-roommate-select-all {
            margin-bottom: 0;
            font-weight: 700;
        }

        .ce-roommate-option {
            margin-bottom: 10px;
            font-weight: 400;
        }

            .ce-roommate-option:last-child {
                margin-bottom: 0;
            }

            .ce-roommate-select-all input,
            .ce-roommate-option input {
                flex: 0 0 auto;
                margin: 0;
                cursor: pointer;
            }

                .ce-roommate-select-all input:disabled,
                .ce-roommate-option input:disabled {
                    cursor: not-allowed;
                }

        .ce-roommate-divider {
            width: 100%;
            height: 1px;
            margin: 2px 0 2px;
            background: #edf0f3;
        }

        .ce-phone-card {
            width: 100%;
            min-width: 280px;
            max-width: 360px;
            padding: 16px;
            background: #ffffff;
            border-radius: 16px;
            box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
            box-sizing: border-box;
            font-size: 13px;
        }
    .ce-phone-question {
        margin: -14px -14px 12px -14px;
        padding: 12px 14px;

        background: linear-gradient(
            135deg,
            rgb(15, 102, 184),
            rgb(18, 35, 65)
        );

        color: #ffffff;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.4;

        border-radius: 12px 12px 0 0;
    }

        .ce-phone-row {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            box-sizing: border-box;
        }

        .ce-country-dropdown {
            width: 135px;
            min-width: 135px;
            max-width: 135px;
            flex: 0 0 135px;
        }

        .ce-phone-input {
            flex: 1;
            min-width: 0;
            width: 100%;
            height: 38px;
            padding: 7px 9px;
            border: 1px solid #cbd5e1;
            border-radius: 7px;
            box-sizing: border-box;
            color: #1e2d45;
            background: #ffffff;
            font-size: 12px;
            outline: none;
        }

            .ce-phone-input:focus {
                border-color: #0f66b8;
            }

        .ce-phone-error {
            margin-top: 6px;
            color: #dc2626;
            font-size: 11px;
        }

        .ce-phone-submit {
            width: 100%;
            margin-top: 12px;
        }

        .ce-text-message {
            max-width: 360px;
            padding: 10px 14px;
            background: #ffffff;
            border-radius: 6px 16px 16px 16px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
            color: #333333;
            font-size: 13px;
            line-height: 1.5;
            box-sizing: border-box;
        }

        :deep(.ce-country-dropdown.p-dropdown) {
            width: 135px;
            min-width: 135px;
            max-width: 135px;
            height: 38px;
        }

        :deep(.ce-country-dropdown .p-dropdown-label) {
            padding: 8px 9px;
            font-size: 12px;
        }

        :deep(.p-dropdown-panel) {
            z-index: 99999 !important;
        }


        /* Duplicate Event action - visual only */
        .ce-duplicate-btn {
            min-width: 72px;
            height: 30px;
            padding: 5px 10px;
            border: 1px solid #0f66b8;
            border-radius: 6px;
            background: #0f66b8;
            color: #ffffff;
            font-family: inherit;
            font-size: 10px;
            font-weight: 700;
            line-height: 1;
            cursor: pointer;
            white-space: nowrap;
            box-sizing: border-box;
        }

        .ce-duplicate-btn:hover:not(:disabled) {
            background: #0c559b;
            border-color: #0c559b;
        }

        .ce-duplicate-btn:disabled {
            opacity: .5;
            cursor: not-allowed;
        }

        /* Invitation method - same white form/card feel as guest phone number */
        .ce-invite-card {
            min-width: 280px;
            max-width: 360px;
        }

        .ce-invite-choice-list {
            width: 100%;
            margin-top: 12px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
            box-sizing: border-box;
        }

        .ce-invite-choice-btn {
            width: 100%;
            height: 38px;
            padding: 7px 10px;
            border: 1px solid #cbd5e1;
            border-radius: 7px;
            background: linear-gradient(135deg, rgb(15, 102, 184), rgb(18, 35, 65));
            color: #ffffff;
            font-family: inherit;
            font-size: 12px;
            font-weight: 700;
            cursor: pointer;
            box-sizing: border-box;
        }

        .ce-invite-choice-btn:hover:not(:disabled) {
            opacity: .92;
        }

        .ce-invite-choice-btn:disabled {
            opacity: .5;
            cursor: not-allowed;
        }

        @media (max-width: 480px) {
            .ce-guest-row {
                grid-template-columns: minmax(0, 1fr) 72px;
                gap: 8px;
            }

                .ce-guest-row .ce-pill {
                    min-width: 68px;
                    font-size: 9px;
                }

            .ce-phone-card {
                max-width: 330px;
            }

            .ce-country-dropdown {
                width: 125px;
                min-width: 125px;
                max-width: 125px;
                flex-basis: 125px;
            }

            :deep(.ce-country-dropdown.p-dropdown) {
                width: 125px;
                min-width: 125px;
                max-width: 125px;
            }
        }
</style>